import React, { useState, useEffect, useMemo } from 'react'
import {
  useStripe,
  useElements,
  CardElement,
  Elements
} from '@stripe/react-stripe-js'
import { useProfile } from '../hooks/useProfile'
import { useAuth } from '../contexts/AuthContext'
import stripePromise from '../lib/stripe'
import { TRIAL_DURATION_DAYS } from '../services/FirestoreService'
import { getAuthHeaders } from '../lib/apiUtils'
import './PaymentForm.css'

interface PaymentFormProps {
  onPaymentSuccess: () => void
}

type SubscriptionTier = 'basic' | 'premium'

const PaymentFormFallback: React.FC<PaymentFormProps> = ({ onPaymentSuccess }) => {
  const { updateProfile } = useProfile()
  const { signOut, user } = useAuth()
  const [loading, setLoading] = useState(false)

  const handleStartFreeTrial = async () => {
    setLoading(true)
    try {
      await updateProfile({
        has_paid: true,
        payment_method: 'trial',
        subscription_tier: 'premium',
        trial_start_date: new Date()
      })
      onPaymentSuccess()
    } catch (err) {
      console.error('Free trial activation error:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="payment-form">
      <div className="payment-container">
        <div className="payment-header">
          <h2>Welcome to FoodAtHome!</h2>
          <button
            onClick={signOut}
            className="sign-out-link"
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Sign out ({user?.email})
          </button>
        </div>
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '20px' }}>
          Get started with a {TRIAL_DURATION_DAYS}-day free trial. Full access to all premium features.
        </p>
        <button
          className="skip-payment-button"
          onClick={handleStartFreeTrial}
          disabled={loading}
          style={{
            background: '#4CAF50',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '16px',
            width: '100%'
          }}
        >
          {loading ? 'Activating...' : `Start ${TRIAL_DURATION_DAYS}-Day Free Trial`}
        </button>
      </div>
    </div>
  )
}

const PaymentFormContent: React.FC<PaymentFormProps> = ({ onPaymentSuccess }) => {
  const stripe = useStripe()
  const elements = useElements()
  const { updateProfile } = useProfile()
  const { signOut, user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionTier>('premium')
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [showTrialOption, setShowTrialOption] = useState(true)

  const planDetails = useMemo(() => ({
    basic: {
      price: 4.99,
      features: [
        'Unlimited photo uploads',
        'Smart recipe discovery (0-2 ingredients)',
        'Multiple cuisine preferences',
        'Dietary restriction support',
        'Save money on groceries'
      ]
    },
    premium: {
      price: 7.99,
      features: [
        'Everything in Basic',
        'Meal planning (breakfast, lunch, dinner)',
        'Calorie tracking & goals',
        'Macro tracking (protein, carbs, fat)',
        'Nutrition insights',
        'Advanced recipe filtering'
      ]
    }
  }), [])

  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        const baseUrl = process.env.REACT_APP_BACKEND_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001')
        const authHeaders = await getAuthHeaders()
        const response = await fetch(`${baseUrl}/api/create-payment-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
          body: JSON.stringify({
            subscription_tier: selectedPlan
          })
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.message || `Payment initialization failed (${response.status})`)
        }

        const { client_secret } = await response.json()
        setClientSecret(client_secret)
      } catch (err) {
        console.error('Error creating payment intent:', err)
        setError(err instanceof Error ? err.message : 'Failed to initialize payment. Please try again.')
      }
    }

    createPaymentIntent()
  }, [selectedPlan, planDetails])

  const handlePayment = async () => {
    if (!stripe || !elements || !clientSecret) {
      setError('Payment system not ready. Please try again.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const cardElement = elements.getElement(CardElement)

      if (!cardElement) {
        throw new Error('Card element not found')
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
        }
      })

      if (error) {
        throw new Error(error.message)
      }

      if (paymentIntent.status === 'succeeded') {
        // Update user profile with subscription tier and clear trial
        await updateProfile({
          has_paid: true,
          payment_method: 'stripe',
          subscription_tier: selectedPlan,
          trial_start_date: null
        })

        onPaymentSuccess()
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Payment failed. Please try again.'
      setError(errorMessage)
      console.error('Payment error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleStartFreeTrial = async () => {
    setLoading(true)
    setError(null)
    try {
      await updateProfile({
        has_paid: true,
        payment_method: 'trial',
        subscription_tier: 'premium',
        trial_start_date: new Date()
      })
      onPaymentSuccess()
    } catch (err) {
      setError('Failed to start free trial. Please try again.')
      console.error('Free trial activation error:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="payment-form">
      <div className="payment-container">
        <div className="payment-header">
          <h2>Welcome to FoodAtHome!</h2>
          <button
            onClick={signOut}
            className="sign-out-link"
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Sign out ({user?.email})
          </button>
        </div>

        {/* Free Trial Banner - Always visible at top for mobile */}
        {showTrialOption && (
          <div className="free-trial-banner">
            <div className="trial-content">
              <h3>🎉 Start Your {TRIAL_DURATION_DAYS}-Day Free Trial</h3>
              <p>Full access to all premium features. No credit card required.</p>
              <button
                className="trial-button"
                onClick={handleStartFreeTrial}
                disabled={loading}
              >
                {loading ? 'Activating...' : `Start Free ${TRIAL_DURATION_DAYS}-Day Trial`}
              </button>
            </div>
            <button
              className="show-plans-link"
              onClick={() => setShowTrialOption(false)}
            >
              Or view paid plans →
            </button>
          </div>
        )}

        {!showTrialOption && (
          <>
            <button
              className="back-to-trial-link"
              onClick={() => setShowTrialOption(true)}
            >
              ← Back to free trial option
            </button>

            <h3 style={{ textAlign: 'center', marginBottom: '20px', color: '#333' }}>
              Choose Your Subscription Plan
            </h3>

        <div className="plan-selector">
          <div
            className={`plan-option ${selectedPlan === 'basic' ? 'selected' : ''}`}
            onClick={() => setSelectedPlan('basic')}
          >
            <h3>Basic</h3>
            <div className="plan-price">${planDetails.basic.price}/month</div>
            <ul>
              {planDetails.basic.features.map((feature, index) => (
                <li key={index}>✅ {feature}</li>
              ))}
            </ul>
          </div>

          <div
            className={`plan-option ${selectedPlan === 'premium' ? 'selected' : ''}`}
            onClick={() => setSelectedPlan('premium')}
          >
            <div className="popular-badge">Most Popular</div>
            <h3>Premium</h3>
            <div className="plan-price">${planDetails.premium.price}/month</div>
            <ul>
              {planDetails.premium.features.map((feature, index) => (
                <li key={index}>✅ {feature}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="payment-details">
          <div className="price-section">
            <span className="price">${planDetails[selectedPlan].price}/month</span>
            <span className="price-description">Cancel anytime</span>
          </div>

          <div className="card-element-container">
            <label htmlFor="card-element">Credit or Debit Card</label>
            <CardElement
              id="card-element"
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                },
              }}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            className="payment-button"
            onClick={handlePayment}
            disabled={loading || !stripe || !clientSecret}
          >
            {loading
              ? 'Processing Payment...'
              : `Start ${selectedPlan} Plan - $${planDetails[selectedPlan].price}/month`
            }
          </button>

          <p className="payment-note">
            Secure payment powered by Stripe. Cancel anytime. Your payment information is safe and encrypted.
          </p>
        </div>
          </>
        )}

        {error && showTrialOption && <div className="error-message">{error}</div>}
      </div>
    </div>
  )
}

const PaymentForm: React.FC<PaymentFormProps> = ({ onPaymentSuccess }) => {
  if (!stripePromise) {
    return <PaymentFormFallback onPaymentSuccess={onPaymentSuccess} />
  }

  return (
    <Elements stripe={stripePromise}>
      <PaymentFormContent onPaymentSuccess={onPaymentSuccess} />
    </Elements>
  )
}

export default PaymentForm